import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { resolveDevelopmentPathwayPhase } from '../reconcileDevelopmentPathway';
import { effectivePhaseCapacity } from '../projectIntent';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import { buildWaterUseScreeningPresentation, waterScreenAssistText, waterScreenRowText } from '../economicContribution/sedEconomicEvidence';
import { isCurrentWaterUseScreeningResult } from '../economicContribution/waterUseScreening';

const MARKER = '\n\nModeled phase water screening\n';
const STAFFING_MARKER = '\n\nModeled phase staffing\n';
const END = '\nThese cumulative phase cases and alternative cooling cases are not additive. They describe data-center water only; separate power-plant process water, utility supply commitments and permitted withdrawals require their own evidence.';

function withoutOwnDisclosure(description: string): string {
  const start = description.indexOf(MARKER);
  if (start < 0) return description;
  const end = description.indexOf(END, start + MARKER.length);
  // Only remove the bounded block generated here; preserve staffing and any
  // authored follow-up that was appended after it.
  return end < 0 ? description : description.slice(0, start) + description.slice(end + END.length);
}

function validRange(value: { low: number; high: number } | null | undefined): boolean {
  return !!value && Number.isFinite(value.low) && Number.isFinite(value.high)
    && value.low >= 0 && value.high >= value.low;
}

/** Append only existing, current, phase-scoped SED results. No demand is
 * calculated here; shared presentation functions serialize the frozen bands.
 * This runs during generation, so saved historical reports remain immutable. */
export function applyPhaseWaterDisclosure(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  intent: ProjectIntent | null | undefined = report.projectIntentAtGen,
  reportType = report.reportType ?? '',
): SiteRagReport {
  if (reportType === 'environmental' && report.waterResources) {
    if (report.waterResources.waterStressAnalysis != null
      && typeof report.waterResources.waterStressAnalysis !== 'string') return report;
    const original = report.waterResources.waterStressAnalysis ?? '';
    const description = withoutOwnDisclosure(original);
    // Environmental reports have no development pathway. Project the same
    // owner phase identities through the same scope checks, then serialize
    // only the resulting bounded blocks into the existing water prose field.
    const projected = applyPhaseWaterDisclosure({ ...report, developmentPathway: (intent?.phases ?? []).map(phase => ({
      phase: phase.label || `Phase ${phase.sequence}`, title: `Phase ${phase.sequence}${phase.label ? ` (${phase.label})` : ''}`, description: '',
    })) }, sed, intent, 'feasibility');
    const blocks = (projected.developmentPathway ?? []).flatMap(row => {
      const start = row.description.indexOf(MARKER);
      const end = row.description.indexOf(END, start + MARKER.length);
      return start >= 0 && end >= 0 ? [`${row.phase}\n${row.description.slice(start + MARKER.length, end)}`] : [];
    });
    const next = blocks.length ? `${description}${MARKER}${blocks.join('\n\n')}${END}` : description;
    return next === original ? report : { ...report, references: projected.references,
      waterResources: { ...report.waterResources, waterStressAnalysis: next } };
  }
  if (reportType !== 'feasibility' || (!Array.isArray(report.developmentPathway) || !report.developmentPathway.length)) return report;
  const data = sed?.economicEvidence?.data;
  const usable = !!sed && !!report.siteId
    && (!report.sedId || report.sedId === sed.id)
    && (report.sedVersionUsed == null || report.sedVersionUsed === sed.version);
  const references = [...(report.references ?? [])];
  const developmentPathway = report.developmentPathway.map(row => {
    // Generated rows can omit narrative fields even after a review pass.
    // Withhold projection on malformed rows without inventing identity/prose;
    // retain the original row for the existing quality/schema diagnostics.
    if (!row || typeof row !== 'object' || typeof row.phase !== 'string'
      || typeof row.title !== 'string' || typeof row.description !== 'string') return row;
    const description = withoutOwnDisclosure(row.description);
    const phase = resolveDevelopmentPathwayPhase(row, intent?.phases ?? []);
    const capacity = phase ? effectivePhaseCapacity(phase) : null;
    const cases = usable && phase && capacity?.range
      ? (data?.waterUseScenarioScreens ?? []).filter(item => {
        const water = item?.result;
        const load = water?.loadRange;
        if (!water || water.status !== 'calculated' || water.siteId !== report.siteId
          || !isCurrentWaterUseScreeningResult(water) || !load
          || !['it', 'facility'].includes(load.basis)
          || ['declared-project', 'whole-campus', 'unknown'].includes(load.phaseBasis)
          || item.scenarioId !== `${phase.id}:${load.basis}` || load.phaseBasis !== phase.id
          || !validRange(load) || load.low <= 0
          || load.low !== capacity.range!.min || load.high !== capacity.range!.max
          || !Array.isArray(water.scenarios) || !water.scenarios.length
          || !water.resolvedContext || !validRange(water.resolvedContext.pue)
          || water.resolvedContext.pue.low < 1
          || !validRange(water.resolvedContext.utilization)
          || water.resolvedContext.utilization.high > 1
          || !validRange(water.resolvedContext.itLoadMw) || !validRange(water.resolvedContext.facilityHeatMw)
          || !water.scenarios.every(scenario => !!scenario && validRange(scenario.averageGallonsPerDay)
            && validRange(scenario.annualGallons) && validRange(scenario.coefficient)
            && (!scenario.fullLoadGallonsPerDay || validRange(scenario.fullLoadGallonsPerDay))
            && Array.isArray(scenario.sourceRefs) && scenario.sourceRefs.length > 0
            && Array.isArray(scenario.assumptions) && Array.isArray(scenario.limitations))) return false;
        const basisUnknown = isIntentFieldExplicitlyUnknown(intent, 'powerBasis');
        if (basisUnknown && load.basisSource === 'site-record') return false;
        if (intent?.powerBasis && !basisUnknown
          && load.basis !== (intent.powerBasis === 'it-load' ? 'it' : 'facility')) return false;
        const adoptedPue = phase.adoptSimulatedPue && typeof phase.simulatedPueValue === 'number'
          && Number.isFinite(phase.simulatedPueValue) && phase.simulatedPueValue >= 1 ? phase.simulatedPueValue : undefined;
        const pueUnknown = isIntentFieldExplicitlyUnknown(intent, 'pueValue');
        if (pueUnknown && adoptedPue == null && water.resolvedContext.pue.source === 'site-record') return false;
        const pue = adoptedPue ?? (!pueUnknown ? intent?.pueValue ?? intent?.pueTarget : undefined);
        if (pue != null && (water.resolvedContext.pue.low !== pue || water.resolvedContext.pue.high !== pue)) return false;
        const utilizationUnknown = isIntentFieldExplicitlyUnknown(intent, 'annualDemandUtilizationRangePct');
        if (utilizationUnknown && water.resolvedContext.utilization.source === 'site-record') return false;
        const utilization = !utilizationUnknown
          ? intent?.annualDemandUtilizationRangePct : undefined;
        if (utilization && (water.resolvedContext.utilization.low !== utilization.low / 100
          || water.resolvedContext.utilization.high !== utilization.high / 100)) return false;
        const heatRejection = phase.bopHeatRejection ?? intent?.bopHeatRejection;
        return !heatRejection || heatRejection === 'unknown' || water.resolvedContext.heatRejection === heatRejection;
      }) : [];
    const blocks: string[] = [];
    for (const item of cases) {
      let presentation: ReturnType<typeof buildWaterUseScreeningPresentation>;
      try {
        presentation = buildWaterUseScreeningPresentation({ waterUseScreening: item.result, waterScreeningSources: data?.waterScreeningSources });
      } catch { continue; } // Malformed frozen cases never become report prose.
      if (!presentation?.sources.length || presentation.sources.some(source => !source.url || !source.citation)) continue;
      const supplyingUrls = new Set(presentation.sources.map(source => source.url));
      if (item.result.scenarios.some(scenario => scenario.sourceRefs.some(source => !supplyingUrls.has(source.url)))) continue;
      const citationByUrl = new Map<string, string>();
      const sourceCitations = presentation.sources.map(source => {
        const field = 'economicEvidence.phaseWaterScreening';
        let index = references.findIndex(ref => ref.url === source.url
          && (ref.source === source.citation || ref.field.startsWith('economicEvidence.waterUseScreening.'))
          && ref.qualification === 'estimated' && ref.sourceGrade === 'inference');
        const frozenSource = data?.waterScreeningSources?.find(value => value.url === source.url)
          ?? item.result.scenarios.flatMap(value => [...value.sourceRefs]).find(value => value.url === source.url);
        if (index < 0) index = references.push({ field, source: source.citation, url: source.url,
          retrievedAt: frozenSource?.accessedAt ?? report.createdAt, qualification: 'estimated', sourceGrade: 'inference' }) - 1;
        const citation = `[Ref ${index + 1}]`;
        citationByUrl.set(source.url, citation);
        return citation;
      }).join(' ');
      const cite = (urls: readonly string[]) => [...new Set(urls)].map(url => citationByUrl.get(url)).filter(Boolean).join(' ');
      blocks.push(`Modeled ${presentation.scope}. ${sourceCitations}`, presentation.lead,
        ...presentation.basis.map(value => `${value} ${sourceCitations}`),
        ...presentation.rows.flatMap(value => {
          const scenario = item.result.scenarios.find(candidate => candidate.scenarioId === value.scenarioId)!;
          return [`Modeled ${waterScreenRowText(value)} ${cite(scenario.sourceRefs.map(source => source.url))}`,
            ...(value.assist ? [`${waterScreenAssistText(value.assist)} ${cite(scenario.adiabaticWaterScreening?.status === 'calculated'
              ? scenario.adiabaticWaterScreening.provenance.sources.map(source => source.url) : [])}`] : [])];
        }),
        ...presentation.caveats, `Methodology and benchmark sources: ${sourceCitations}`);
    }
    // Staffing owns a trailing block too. Keep water ahead of it so both
    // projections remain idempotent when the final generation wrapper repeats.
    const staffingAt = description.indexOf(STAFFING_MARKER);
    const anchor = staffingAt < 0 ? description.length : staffingAt;
    const next = blocks.length
      ? `${description.slice(0, anchor)}${MARKER}${blocks.join('\n')}${END}${description.slice(anchor)}` : description;
    return next === row.description ? row : { ...row, description: next };
  });
  return developmentPathway.some((row, index) => row !== report.developmentPathway![index])
    ? { ...report, developmentPathway, references } : report;
}
